import Icon from '@components/utils/icons/icon';
import NewTabLink from '@components/utils/links/newTabLink';
import { Caption } from '@components/utils/text';
import { StyledProps, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
} from '@enums';
import { AdditionalProductFragment } from '@graphql/generated/graphql';
import StrapiImage from '@lib/image/strapiImage';
import { getStrapiAssetUrl } from '@lib/strapi';
import { Card, CardProps, HStack, lineClamp } from '@lib/uikit';
import { useMemo } from 'react';

interface Props extends CardProps {
    additionalProduct: AdditionalProductFragment;
}

const SCard = styled(Card)`
    width: 100%;
    height: 100%;
    align-content: center;
`;

const SHStack = styled(HStack)`
    gap: 0.75rem;
`;

const SStrapiImage = styled(StrapiImage)`
    display: flex;
    width: 4rem;
    height: 4rem;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.shape.radius.default};
`;

const TextContainer = styled.div<StyledProps<{ color: string }>>`
    flex: 1;
    width: fit-content;
    color: ${({ color }) => color};
`;

const Title = styled.p`
    color: inherit;
    line-height: 1.25rem !important;
    font: ${({ theme }) => theme.fonts.subtitle5};
    ${lineClamp(1)}
`;

const Description = styled(Caption)`
    ${lineClamp(2)}
`;

const SIcon = styled(Icon)`
    height: 1.25rem;
    width: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-inline-end: ${({ theme }) => theme.spacing.xs};
`;

export const AdditionalProductBanner = ({
    additionalProduct,
    ...cardProps
}: Props) => {
    const { palette } = useTheme();
    const color = useMemo(
        () =>
            cardProps.variant === 'blurred'
                ? palette.common.white
                : palette.common.black,
        [cardProps.variant, palette.common.black, palette.common.white]
    );

    if (!additionalProduct.Url) return null;

    return (
        <NewTabLink
            href={additionalProduct.Url}
            destination_name={additionalProduct.Name}
            destination_category={
                OutboundLinkDestinationCategory.AdditionalProduct
            }
            context={OutboundLinkContext.StoryPage}
            shape={OutboundLinkShape.Banner}
        >
            <SCard {...cardProps} padding='xs'>
                <SHStack>
                    <SStrapiImage
                        sizes='4rem'
                        src={getStrapiAssetUrl(additionalProduct.Image)}
                        formats={additionalProduct.Image?.formats}
                        alt={
                            additionalProduct.Image?.alternativeText ??
                            additionalProduct.Name
                        }
                    />
                    <TextContainer color={color}>
                        <Title>{additionalProduct.Name}</Title>
                        {additionalProduct.Description && (
                            <Description color={color}>
                                {additionalProduct.Description}
                            </Description>
                        )}
                    </TextContainer>
                    <SIcon name='open_in_new' color={color} />
                </SHStack>
            </SCard>
        </NewTabLink>
    );
};
